import React, { useRef, useState, useCallback } from "react";
import { Spinner } from "react-bootstrap";
import {
  BsTrash, BsCloudUploadFill,
  BsFileEarmarkPdf, BsFileEarmarkImage, BsFileEarmarkExcel,
  BsFileEarmarkWord, BsFileEarmarkText, BsFileEarmark,
} from "react-icons/bs";
import { toast } from "react-toastify";
import { 
  useGetProjetFichiers, 
  useUploadProjetFile, 
  useDeleteProjetFile, 
  useDownloadProjetFile 
} from "../../hooks/PROJET/useGetProjetFichiers";
import ConfirmModal from "../../components/Modals/ConfirmModal";

const ALLOWED_TYPES = [
  "image/jpeg", "image/jpg", "image/png", "image/gif", "image/webp",
  "image/heic", "image/heif",
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "text/plain", "text/csv",
];
const ALLOWED_EXTS = ["jpg","jpeg","png","gif","webp","heic","heif","pdf","doc","docx","xls","xlsx","txt","csv"];
const MAX_MB = 20;

const EXT_ICON_MAP = {
  pdf:  <BsFileEarmarkPdf   className="text-danger"  size={16} />,
  jpg:  <BsFileEarmarkImage className="text-primary" size={16} />,
  jpeg: <BsFileEarmarkImage className="text-primary" size={16} />,
  png:  <BsFileEarmarkImage className="text-primary" size={16} />,
  gif:  <BsFileEarmarkImage className="text-primary" size={16} />,
  webp: <BsFileEarmarkImage className="text-primary" size={16} />,
  heic: <BsFileEarmarkImage className="text-primary" size={16} />,
  heif: <BsFileEarmarkImage className="text-primary" size={16} />,
  xls:  <BsFileEarmarkExcel className="text-success" size={16} />,
  xlsx: <BsFileEarmarkExcel className="text-success" size={16} />,
  doc:  <BsFileEarmarkWord  className="text-primary" size={16} />,
  docx: <BsFileEarmarkWord  className="text-primary" size={16} />,
  csv:  <BsFileEarmarkText  className="text-success" size={16} />,
  txt:  <BsFileEarmarkText  className="text-muted"   size={16} />,
};

const getIcon = (name = "") => EXT_ICON_MAP[name.split(".").pop().toLowerCase()] ?? <BsFileEarmark className="text-muted" size={16} />;
const humanSize = (b) => !b ? "" : b < 1024 ? `${b} B` : b < 1048576 ? `${(b/1024).toFixed(1)} Ko` : `${(b/1048576).toFixed(1)} Mo`;

const ProjetFichiers = ({ projetId, canUpload = false, canDelete = false }) => {
  const fileInputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [pendingFileId, setPendingFileId] = useState(null);
  const [deletingId,    setDeletingId]    = useState(null);
  const [uploading,     setUploading]     = useState(false);

  // ── Hooks (TanStack Query) ──────────────────────────────────────────────────
  const { data, isLoading } = useGetProjetFichiers(projetId);
  const fichiers = Array.isArray(data) ? data : (data?.data ?? []);

  const upload = useUploadProjetFile(projetId);
  const remove = useDeleteProjetFile(projetId);
  const { download } = useDownloadProjetFile(projetId);

  // ── Validation ──────────────────────────────────────────────────────────────
  const validate = (file) => {
    const type = file.type || "";
    if (type && !ALLOWED_TYPES.includes(type)) return `Type non autorisé : ${file.name}`;
    if (!type) {
      const ext = file.name.split(".").pop().toLowerCase();
      if (!ALLOWED_EXTS.includes(ext)) return `Type non autorisé : ${file.name}`;
    }
    if (file.size > MAX_MB * 1024 * 1024) return `Fichier trop lourd (max ${MAX_MB} Mo)`;
    return null;
  };

  // ── Handlers ────────────────────────────────────────────────────────────────
  const handleFiles = useCallback(async (fileList) => {
    const files = Array.from(fileList);
    if (files.length === 0) return;
    const valid = [];
    for (const file of files) {
      const err = validate(file);
      if (err) toast.error(err);
      else valid.push(file);
    }
    if (valid.length === 0) return;
    setUploading(true);
    let successCount = 0;
    for (const file of valid) {
      try {
        await upload.mutateAsync(file);
        successCount++;
      } catch (e) {
        toast.error(`Erreur lors de l'envoi de ${file.name} : ${e.message}`);
      }
    }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (successCount > 0)
      toast.success(successCount === 1 ? "Fichier ajouté !" : `${successCount} fichiers ajoutés !`);
  }, [upload]);

  const doDelete = async () => {
    const fileId = pendingFileId;
    setPendingFileId(null);
    setDeletingId(fileId);

    remove.mutate(fileId, {
      onSuccess: () => toast.success("Fichier supprimé"),
      onError: (e) => toast.error(`Erreur : ${e.message}`),
      onSettled: () => setDeletingId(null),
    });
  };

  const onDragOver  = (e) => { e.preventDefault(); setDragOver(true); };
  const onDragLeave = ()  => setDragOver(false);
  const onDrop      = (e) => { 
    e.preventDefault(); 
    setDragOver(false); 
    if (canUpload) handleFiles(e.dataTransfer.files); 
  };

  return (
    <>
      {/* ── Drop zone ── */}
      {canUpload && (
        <div
          className={`rounded p-3 text-center mb-3 border ${dragOver ? "border-info bg-info bg-opacity-10" : "border-secondary border-opacity-25"}`}
          style={{ cursor: "pointer", transition: "all .15s", borderStyle: "dashed" }}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          onDrop={onDrop}
          onClick={() => !uploading && fileInputRef.current?.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept={ALLOWED_TYPES.join(",")}
            multiple
            style={{ display: "none" }}
            onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }}
          />
          {uploading ? (
            <Spinner size="sm" animation="border" style={{ color: "#f6c23e" }} />
          ) : (
            <>
              <BsCloudUploadFill size={20} className="text-info mb-1" />
              <div style={{ fontSize: "0.78rem" }} className="text-muted">
                Glissez des fichiers ou <span className="text-info fw-semibold">parcourir</span>
                <br /><span style={{ fontSize: "0.7rem" }}>PDF, Word, Excel, images — max {MAX_MB} Mo</span>
              </div>
            </>
          )}
        </div>
      )}

      {/* ── Loading State ── */}
      {isLoading && (
        <div className="text-center py-2">
          <Spinner size="sm" animation="border" style={{ color: "#4e73df" }} />
        </div>
      )}

      {/* ── File List ── */}
      {!isLoading && (
        fichiers.length === 0 ? (
          <p className="text-muted small mb-0">Aucun fichier joint.</p>
        ) : (
          <ul className="list-unstyled mb-0">
            {fichiers.map((f) => (
              <li key={f.id} className="d-flex align-items-center justify-content-between py-1 border-bottom">
                <div className="d-flex align-items-center gap-2 text-truncate" style={{ minWidth: 0 }}>
                  <span className="flex-shrink-0">{getIcon(f.name)}</span>
                  <button
                    className="btn btn-link p-0 text-truncate small text-decoration-none text-start"
                    style={{ maxWidth: 160 }}
                    title={f.name}
                    onClick={() => download(f.id, f.name)} 
                  >
                    {f.name}
                  </button>
                  {f.size && (
                    <span className="text-muted flex-shrink-0" style={{ fontSize: "0.7rem", whiteSpace: "nowrap" }}>
                      {humanSize(f.size)}
                    </span>
                  )}
                </div>
                {canDelete && (
                  <button
                    className="btn btn-link btn-sm text-danger p-0 ms-2 flex-shrink-0"
                    title="Supprimer"
                    disabled={deletingId === f.id}
                    onClick={() => setPendingFileId(f.id)}
                  >
                    {deletingId === f.id ? (
                      <Spinner size="sm" animation="border" />
                    ) : (
                      <BsTrash size={13} />
                    )}
                  </button>
                )}
              </li>
            ))}
          </ul>
        )
      )}

      <ConfirmModal
        show={pendingFileId !== null}
        onHide={() => setPendingFileId(null)}
        onConfirm={doDelete}
        title="Supprimer le fichier"
        message="Êtes-vous sûr de vouloir supprimer ce fichier ?"
        confirmLabel="Supprimer"
        confirmVariant="danger"
      />
    </>
  );
};

export default ProjetFichiers;